/**
 * AutoGrab fleet vehicle verification (CUR-1177) — constants shared by the
 * console (fleet record, claim Vehicle tab, admin integrations), the server
 * (gates, lookup service) and the identity resolver.
 */

/**
 * Default-off PostHog rollout flag. Server procedures return NOT_FOUND while
 * it is off; the client hides every AutoGrab surface. Both gates are required
 * alongside an ENABLED, credentialed AUTOGRAB integration profile.
 */
export const ENABLE_AUTOGRAB_VERIFICATION_FLAG = 'enable-autograb-verification';

/** Provider region sent on every lookup; AU-only in this slice. */
export const AUTOGRAB_REGION = 'au';

/**
 * Initial egress ceiling from the ticket (requests per second, per provider
 * account). The trial account's actual product limits may be lower; the
 * server honours provider reset headers on 429 regardless.
 */
export const AUTOGRAB_DEFAULT_MAX_REQUESTS_PER_SECOND = 5;

/** Fixed, allow-listed API origin — never supplied by a browser or a tenant. */
export const AUTOGRAB_API_ORIGIN = 'https://api.autograb.com.au';

/** Provider endpoint templates recorded on the lookup ledger (never concrete URLs). */
export const AUTOGRAB_ENDPOINT_TEMPLATES = {
    VIN: '/v2/vehicles/vins/{vin}',
    REGISTRATION: '/v2/vehicles/registrations/{plate}',
} as const;

/**
 * Effective verification status of a fleet vehicle as shown in the console.
 *   - UNVERIFIED: no accepted AutoGrab identity.
 *   - VERIFIED: accepted identity whose lookup identity still matches the file.
 *   - NEEDS_VERIFICATION: accepted identity exists but the file's lookup
 *     identity (VIN, or plate + state) changed since — file identity is used
 *     until verified again (plan open decision 1, provisional).
 */
export const FLEET_VEHICLE_VERIFICATION_STATUSES = ['UNVERIFIED', 'VERIFIED', 'NEEDS_VERIFICATION'] as const;
export type FleetVehicleVerificationStatus = (typeof FLEET_VEHICLE_VERIFICATION_STATUSES)[number];

export const FLEET_VEHICLE_VERIFICATION_STATUS_LABELS = {
    UNVERIFIED: 'Unverified',
    VERIFIED: 'Verified by AutoGrab',
    NEEDS_VERIFICATION: 'Needs verification',
} as const satisfies Record<FleetVehicleVerificationStatus, string>;

/**
 * CUR-1254 — the claim-owned vehicle identity. `VERIFIED` means the build was
 * confirmed against AutoGrab (live, or reused from an already-verified linked
 * fleet vehicle); `UNVERIFIED` means the claim carries lodged details only,
 * so a push job must not present them as provider-confirmed.
 */
export const CLAIM_VEHICLE_IDENTITY_STATUSES = ['VERIFIED', 'UNVERIFIED'] as const;
export type ClaimVehicleIdentityStatus = (typeof CLAIM_VEHICLE_IDENTITY_STATUSES)[number];

export const CLAIM_VEHICLE_IDENTITY_STATUS_LABELS = {
    VERIFIED: 'Verified by AutoGrab',
    UNVERIFIED: 'Unverified',
} as const satisfies Record<ClaimVehicleIdentityStatus, string>;

/** Where the claim's identity came from; one identity shape for all three. */
export const CLAIM_VEHICLE_IDENTITY_ORIGINS = ['LIVE_LOOKUP', 'FLEET_VERIFICATION', 'LODGED_DETAILS'] as const;
export type ClaimVehicleIdentityOrigin = (typeof CLAIM_VEHICLE_IDENTITY_ORIGINS)[number];

export const CLAIM_VEHICLE_IDENTITY_ORIGIN_LABELS = {
    LIVE_LOOKUP: 'Confirmed with AutoGrab on this claim',
    FLEET_VERIFICATION: 'Reused from the verified fleet vehicle',
    LODGED_DETAILS: 'Lodged details, not verified',
} as const satisfies Record<ClaimVehicleIdentityOrigin, string>;
